import { useTranslation } from 'react-i18next'
import { Container } from '../ui/Container'
import { Skeleton } from '../ui/Skeleton'

/**
 * What a code-split route shows while its chunk downloads.
 *
 * Two things this is deliberately NOT:
 *
 * A SPINNER. The chunk usually arrives in well under a second on a warm
 * connection, and a spinner that flashes for 200 ms reads as a glitch. A block
 * roughly the shape of the page that is coming reads as the page arriving.
 *
 * EMPTY. Rendering nothing collapses the layout to zero height, the footer jumps
 * up to meet the header, and then everything shoves back down when the chunk
 * lands — a large, avoidable layout shift on a metric Docs/03 budgets for. The
 * min-height holds the space open.
 *
 * It announces itself once, through the region rather than through each bar, so
 * a screen reader says "Loading" and not "Loading" eight times.
 */
export const RouteFallback = () => {
  const { t } = useTranslation()

  return (
    <Container className="min-h-[60vh] py-10" as="section">
      <div role="status" aria-live="polite" className="space-y-6">
        <span className="sr-only">{t('common.loading')}</span>

        <div className="space-y-3">
          <Skeleton className="h-9 w-2/3 max-w-md" />
          <Skeleton className="h-5 w-full max-w-xl" />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-40 w-full" />
          ))}
        </div>
      </div>
    </Container>
  )
}
